const { normalizeLanguage } = require('../i18n/translations');

const METHOD_LABELS = {
  en: {
    title: 'Randomization Methodology',
    study: 'Study',
    untitled: 'Untitled study',
    generatedWith: 'Generated with',
    generatedAt: 'Generated at',
    technique: 'Technique',
    seed: 'Seed',
    signature: 'Experiment signature',
    methodDescription: 'Method description',
    parameters: 'Parameters',
    participants: 'Participants',
    arms: 'Arms',
    armLabels: 'Arm labels',
    randomizationUnit: 'Randomization unit',
    balanceUnit: 'Balance unit',
    techniqueId: 'Technique ID',
    validationSummary: 'Validation summary',
    armCounts: 'Arm counts',
    imbalance: 'Imbalance',
    validationStatus: 'Validation status',
    warnings: 'Warnings',
    noWarnings: 'No validation warnings were generated.',
    verification: 'Verification',
    reproduce: 'To reproduce this list, open randFlow, enter the same technique, parameters, and seed, then compare the generated experiment signature with the signature above.'
  },
  pt: {
    title: 'Metodologia de Randomização',
    study: 'Estudo',
    untitled: 'Estudo sem título',
    generatedWith: 'Gerado com',
    generatedAt: 'Gerado em',
    technique: 'Técnica',
    seed: 'Seed',
    signature: 'Assinatura do experimento',
    methodDescription: 'Descrição do método',
    parameters: 'Parâmetros',
    participants: 'Participantes',
    arms: 'Braços',
    armLabels: 'Rótulos dos braços',
    randomizationUnit: 'Unidade de randomização',
    balanceUnit: 'Unidade de balanceamento',
    techniqueId: 'ID da técnica',
    validationSummary: 'Resumo de validação',
    armCounts: 'Contagem por braço',
    imbalance: 'Desequilíbrio',
    validationStatus: 'Status de validação',
    warnings: 'Avisos',
    noWarnings: 'Nenhum aviso de validação foi gerado.',
    verification: 'Verificação',
    reproduce: 'Para reproduzir esta lista, abra o randFlow, informe a mesma técnica, parâmetros e seed, e compare a assinatura gerada com a assinatura acima.'
  },
  es: {
    title: 'Metodología de Aleatorización',
    study: 'Estudio',
    untitled: 'Estudio sin título',
    generatedWith: 'Generado con',
    generatedAt: 'Generado en',
    technique: 'Técnica',
    seed: 'Seed',
    signature: 'Firma del experimento',
    methodDescription: 'Descripción del método',
    parameters: 'Parámetros',
    participants: 'Participantes',
    arms: 'Brazos',
    armLabels: 'Rótulos de los brazos',
    randomizationUnit: 'Unidad de aleatorización',
    balanceUnit: 'Unidad de balance',
    techniqueId: 'ID de la técnica',
    validationSummary: 'Resumen de validación',
    armCounts: 'Conteo por brazo',
    imbalance: 'Desequilibrio',
    validationStatus: 'Estado de validación',
    warnings: 'Avisos',
    noWarnings: 'No se generaron avisos de validación.',
    verification: 'Verificación',
    reproduce: 'Para reproducir esta lista, abra randFlow, informe la misma técnica, parámetros y seed, y compare la firma generada con la firma anterior.'
  }
};

const TECHNIQUE_DESCRIPTIONS = {
  en: {
    simple: 'Participants were independently assigned to study arms using seed-based simple randomization with equal allocation probability across arms.',
    blocked: (result) => `Participants were assigned using fixed block randomization with block size ${result.parameters.blockSize}. Each complete block contains balanced arm assignments shuffled with the documented seed.`,
    'random-block': (result) => `Participants were assigned using random block randomization. For each block, the block size was selected from ${result.parameters.blockSizes.join(', ')} and the balanced assignments were shuffled with the documented seed.`,
    clustered: 'Clusters were randomized as intact units. All participants within a cluster received the same arm assignment, preserving cluster boundaries.',
    'stratified-blocked': (result) => `Participants were randomized within each stratum using fixed block randomization with block size ${result.parameters.blockSize}. The participant is the unit of randomization, and each stratum is balanced independently.`,
    'stratified-random-block': (result) => `Participants were randomized within each stratum using random block sizes selected from ${result.parameters.blockSizes.join(', ')}. The participant is the unit of randomization, and each stratum is balanced independently.`,
    'cluster-stratum': (result) => `Participants were randomized within each cluster/center using random block sizes selected from ${result.parameters.blockSizes.join(', ')}. The cluster/center is used as a stratum for balance, not as a single-treatment randomization unit.`,
    'cluster-stratified': 'Clusters were grouped by stratum, then whole clusters were randomized within each stratum. The cluster is the unit of randomization, so all participants in the same cluster received the same arm.'
  },
  pt: {
    simple: 'Participantes foram alocados independentemente aos braços do estudo usando randomização simples baseada em seed, com probabilidade igual entre os braços.',
    blocked: (result) => `Participantes foram alocados por randomização em blocos fixos com tamanho ${result.parameters.blockSize}. Cada bloco completo contém alocações balanceadas embaralhadas com a seed documentada.`,
    'random-block': (result) => `Participantes foram alocados por randomização em blocos aleatórios. Em cada bloco, o tamanho foi selecionado entre ${result.parameters.blockSizes.join(', ')} e as alocações balanceadas foram embaralhadas com a seed documentada.`,
    clustered: 'Clusters foram randomizados como unidades intactas. Todos os participantes dentro de um cluster receberam o mesmo braço, preservando os limites do cluster.',
    'stratified-blocked': (result) => `Participantes foram randomizados dentro de cada estrato usando blocos fixos com tamanho ${result.parameters.blockSize}. O participante é a unidade de randomização, e cada estrato é balanceado independentemente.`,
    'stratified-random-block': (result) => `Participantes foram randomizados dentro de cada estrato usando tamanhos de bloco aleatórios selecionados entre ${result.parameters.blockSizes.join(', ')}. O participante é a unidade de randomização, e cada estrato é balanceado independentemente.`,
    'cluster-stratum': (result) => `Participantes foram randomizados dentro de cada cluster/centro usando tamanhos de bloco aleatórios selecionados entre ${result.parameters.blockSizes.join(', ')}. O cluster/centro é usado como estrato de balanceamento, não como unidade de tratamento único.`,
    'cluster-stratified': 'Clusters foram agrupados por estrato e então clusters inteiros foram randomizados dentro de cada estrato. O cluster é a unidade de randomização, então todos os participantes do mesmo cluster receberam o mesmo braço.'
  },
  es: {
    simple: 'Los participantes fueron asignados independientemente a los brazos del estudio usando aleatorización simple basada en seed, con probabilidad igual entre brazos.',
    blocked: (result) => `Los participantes fueron asignados por aleatorización en bloques fijos con tamaño ${result.parameters.blockSize}. Cada bloque completo contiene asignaciones balanceadas mezcladas con la seed documentada.`,
    'random-block': (result) => `Los participantes fueron asignados por aleatorización en bloques aleatorios. En cada bloque, el tamaño fue seleccionado entre ${result.parameters.blockSizes.join(', ')} y las asignaciones balanceadas fueron mezcladas con la seed documentada.`,
    clustered: 'Los clusters fueron aleatorizados como unidades intactas. Todos los participantes dentro de un cluster recibieron el mismo brazo, preservando los límites del cluster.',
    'stratified-blocked': (result) => `Los participantes fueron aleatorizados dentro de cada estrato usando bloques fijos con tamaño ${result.parameters.blockSize}. El participante es la unidad de aleatorización, y cada estrato se balancea independientemente.`,
    'stratified-random-block': (result) => `Los participantes fueron aleatorizados dentro de cada estrato usando tamaños de bloque aleatorios seleccionados entre ${result.parameters.blockSizes.join(', ')}. El participante es la unidad de aleatorización, y cada estrato se balancea independientemente.`,
    'cluster-stratum': (result) => `Los participantes fueron aleatorizados dentro de cada cluster/centro usando tamaños de bloque aleatorios seleccionados entre ${result.parameters.blockSizes.join(', ')}. El cluster/centro se usa como estrato de balance, no como unidad de tratamiento único.`,
    'cluster-stratified': 'Los clusters fueron agrupados por estrato y luego clusters completos fueron aleatorizados dentro de cada estrato. El cluster es la unidad de aleatorización, por lo que todos los participantes del mismo cluster recibieron el mismo brazo.'
  }
};

function describeTechnique(result, language = 'en') {
  const descriptions = TECHNIQUE_DESCRIPTIONS[normalizeLanguage(language)];
  const description = descriptions[result.algorithmId];

  if (typeof description === 'function') {
    return description(result);
  }

  if (description) {
    return description;
  }

  return descriptions.simple;
}

function resultToMethodsText(result, signature, options = {}) {
  const language = options.language ? normalizeLanguage(options.language) : 'en';
  const labels = METHOD_LABELS[language];
  const warnings = result.metrics.warnings.length
    ? result.metrics.warnings.join(' ')
    : labels.noWarnings;
  const getArmLabel = (index) => result.parameters.armLabels[index] || `Arm ${index + 1}`;

  return `${labels.title}

${labels.study}: ${result.parameters.studyName || labels.untitled}
${labels.generatedWith}: ${result.version}
${labels.generatedAt}: ${result.timestamp}
${labels.technique}: ${result.algorithm}
${labels.seed}: ${result.seed}
${labels.signature}: ${signature}

${labels.methodDescription}:
${describeTechnique(result, language)}

${labels.parameters}:
- ${labels.participants}: ${result.parameters.n}
- ${labels.arms}: ${result.parameters.arms}
- ${labels.armLabels}: ${result.parameters.armLabels.join('; ')}
- ${labels.randomizationUnit}: ${result.parameters.randomizationUnit || (result.algorithmId === 'clustered' ? 'cluster' : 'participant')}
- ${labels.balanceUnit}: ${result.parameters.balanceUnit || 'overall'}
- ${labels.techniqueId}: ${result.algorithmId}

${labels.validationSummary}:
- ${labels.armCounts}: ${result.metrics.armCounts.map((count, index) => `${getArmLabel(index)} = ${count}`).join('; ')}
- ${labels.imbalance}: ${result.metrics.imbalance} (${result.metrics.imbalancePercent}%)
- ${labels.validationStatus}: ${result.metrics.validation}
- ${labels.warnings}: ${warnings}

${labels.verification}:
${labels.reproduce}
`;
}

module.exports = {
  describeTechnique,
  METHOD_LABELS,
  resultToMethodsText
};
